// Sizing rules for the free calculators (/tools/*) — ONE implementation shared
// by the React pages and the prerendered bodies (scripts/prerender-body.mjs), so
// the static reference tables a crawler reads are computed by the same code a
// visitor runs.
//
// Every rule restates a published guide; change them together:
//   rugs          -> /ideas/rug-size-guide
//   chandeliers   -> /ideas/chandelier-size-guide
//   dining tables -> /ideas/dining-table-size-guide
//   wall art      -> /ideas/wall-art-size-guide
//   lampshades    -> /ideas/lampshade-size-guide
// scripts/check-sizing-rules.mjs asserts the guides' own worked examples.

/** Standard rectangular rug sizes, feet, short side first. */
export const RUG_SIZES = [
  [3, 5],
  [4, 6],
  [5, 8],
  [6, 9],
  [8, 10],
  [9, 12],
  [10, 14],
];

/** Standard round rug diameters, feet. */
export const ROUND_RUG_SIZES = [4, 5, 6, 7, 8, 10];

export const rugLabel = ([w, l]) => `${w}×${l}`;
export const rugKey = ([w, l]) => `${w}x${l}`;

const byArea = (a, b) => a[0] * a[1] - b[0] * b[1];

/**
 * Living room, front legs on the rug. Leave 12–18 in. of bare floor at the
 * walls (8 in. is acceptable in small rooms): take the largest standard rug
 * that keeps 18 in., else 12 in., else 8 in. A sofa, when given, needs the rug
 * to run 6–8 in. past each end.
 */
export function livingRoomRug({ roomLengthFt, roomWidthFt, sofaLengthIn }) {
  const shortRoom = Math.min(roomLengthFt, roomWidthFt) * 12;
  const longRoom = Math.max(roomLengthFt, roomWidthFt) * 12;
  if (!(shortRoom > 0 && longRoom > 0)) return null;

  let size = null;
  let marginIn = null;
  for (const margin of [18, 12, 8]) {
    const fits = RUG_SIZES.filter(
      ([w, l]) => w * 12 <= shortRoom - 2 * margin && l * 12 <= longRoom - 2 * margin,
    ).sort(byArea);
    if (fits.length) {
      size = fits[fits.length - 1];
      marginIn = margin;
      break;
    }
  }
  if (!size) return { size: null };

  // Rugs sit with the long side along the sofa. When the sofa outruns the
  // rug, offer the smallest size that clears it by 6 in. per end while still
  // leaving the small-room minimum of 8 in. at the walls.
  const sofaFits = sofaLengthIn > 0 ? size[1] * 12 >= sofaLengthIn + 12 : null;
  const sofaUpgrade =
    sofaFits === false
      ? (RUG_SIZES.filter(
          ([w, l]) => l * 12 >= sofaLengthIn + 12 && w * 12 <= shortRoom - 16 && l * 12 <= longRoom - 16,
        ).sort(byArea)[0] ?? null)
      : null;
  const sofaUpgradeMarginIn = sofaUpgrade
    ? Math.floor(Math.min(shortRoom - sofaUpgrade[0] * 12, longRoom - sofaUpgrade[1] * 12) / 2)
    : null;
  return {
    size,
    marginIn,
    sofaFits,
    sofaUpgrade,
    sofaUpgradeMarginIn,
    allLegsOn: shortRoom >= 12 * 12 && longRoom >= 18 * 12,
  };
}

/** Bedrooms follow the guide's table, not a formula. */
export const BED_RUGS = {
  king: { size: [9, 12], bed: "King", margin: "18–24 in. of rug on each side and at the foot" },
  queen: { size: [8, 10], bed: "Queen", margin: "18–24 in. of rug on each side and at the foot" },
  full: { size: [6, 9], bed: "Full", margin: "at least 12 in. of rug beyond each side" },
  twin: { size: [5, 8], bed: "Twin", margin: "at least 12 in. of rug beyond each side" },
};

/**
 * Dining: add 24–30 in. to every side of the table so chairs stay on the rug
 * when pulled out. `minimum` keeps 24 in., `roomier` keeps 30 in.
 */
export function diningRug({ shape, lengthIn, widthIn, diameterIn }) {
  if (shape === "round") {
    if (!(diameterIn > 0)) return null;
    const pick = (extra) => ROUND_RUG_SIZES.find((d) => d * 12 >= diameterIn + 2 * extra) ?? null;
    return { shape, minimum: pick(24), roomier: pick(30) };
  }
  const shortT = Math.min(lengthIn, widthIn);
  const longT = Math.max(lengthIn, widthIn);
  if (!(shortT > 0 && longT > 0)) return null;
  const pick = (extra) =>
    [...RUG_SIZES].sort(byArea).find(([w, l]) => w * 12 >= shortT + 2 * extra && l * 12 >= longT + 2 * extra) ??
    null;
  return { shape: "rectangle", minimum: pick(24), roomier: pick(30) };
}

/** Room length + width in feet = fixture diameter in inches. */
export function chandelierDiameterIn({ roomLengthFt, roomWidthFt }) {
  const d = Number(roomLengthFt) + Number(roomWidthFt);
  return d > 0 ? Math.round(d) : null;
}

/** Over a table: one-half to two-thirds of the table's width. */
export function chandelierOverTableIn({ tableWidthIn }) {
  if (!(tableWidthIn > 0)) return null;
  return [Math.round(tableWidthIn / 2), Math.round((tableWidthIn * 2) / 3)];
}

/** Standard dining table height the hanging maths assumes. */
export const TABLE_HEIGHT_IN = 30;
export const WALKWAY_CLEARANCE_IN = 84;

/**
 * Hanging height. Over a table: lowest point 30–36 in. above the top with an
 * 8 ft ceiling, plus about 3 in. per foot of ceiling above 8 ft. In walking
 * areas: at least 84 in. from the floor. `maxDropIn` is ceiling-to-lowest-point.
 */
export function chandelierHanging({ ceilingFt }) {
  const ceilingIn = Number(ceilingFt) * 12;
  if (!(ceilingIn > 0)) return null;
  const extra = Math.max(0, Math.round((Number(ceilingFt) - 8) * 3));
  const aboveTable = [30 + extra, 36 + extra];
  return {
    aboveTable,
    tableDropIn: [
      ceilingIn - TABLE_HEIGHT_IN - aboveTable[1],
      ceilingIn - TABLE_HEIGHT_IN - aboveTable[0],
    ],
    walkwayMaxDropIn: ceilingIn - WALKWAY_CLEARANCE_IN,
  };
}

// ---------------------------------------------------------------------------
// Dining tables
// ---------------------------------------------------------------------------

/** The guide's seating tables: [comfortably, at a squeeze]. Rectangles are
 *  36–40 in. wide; the guide's examples pair 60 in. with 36 and 72+ with 40. */
export const RECT_TABLES = [
  { length: 48, width: 36, seats: [4, 4] },
  { length: 60, width: 36, seats: [6, 6] },
  { length: 72, width: 40, seats: [6, 8] },
  { length: 84, width: 40, seats: [8, 8] },
  { length: 96, width: 40, seats: [8, 10] },
  { length: 108, width: 40, seats: [10, 12] },
];
/** The guide groups 36–44 in. rounds; 42 stands for that row. */
export const ROUND_TABLES = [
  { diameter: 42, seats: [4, 4] },
  { diameter: 48, seats: [4, 6] },
  { diameter: 54, seats: [6, 6] },
  { diameter: 60, seats: [6, 8] },
  { diameter: 72, seats: [8, 10] },
];
export const CHAIR_CLEARANCE_IN = 36;
export const WALK_BEHIND_IN = [42, 48];

/** Subtract 36 in. of clearance from every side: the largest footprint. */
export function maxTableFootprint({ roomLengthFt, roomWidthFt }) {
  const short = Math.min(roomLengthFt, roomWidthFt) * 12 - 2 * CHAIR_CLEARANCE_IN;
  const long = Math.max(roomLengthFt, roomWidthFt) * 12 - 2 * CHAIR_CLEARANCE_IN;
  return short > 0 && long > 0 ? [short, long] : null;
}

const tableDims = (t) => (t.diameter ? [t.diameter, t.diameter] : [t.width, t.length]);
export const tableLabel = (t) => (t.diameter ? `${t.diameter} in. round` : `${t.length} × ${t.width} in.`);

/** Clearance from each edge of a table centred in the room, inches. */
export function tableClearance(table, { roomLengthFt, roomWidthFt }) {
  const [w, l] = tableDims(table);
  const short = Math.min(roomLengthFt, roomWidthFt) * 12;
  const long = Math.max(roomLengthFt, roomWidthFt) * 12;
  return { sides: Math.floor((short - w) / 2), ends: Math.floor((long - l) / 2) };
}

/**
 * The smallest table that seats `people` comfortably, then whether the room
 * takes it with 36 in. on every side. If not, the largest table that fits.
 */
export function diningTable({ roomLengthFt, roomWidthFt, shape, people }) {
  const tables = shape === "round" ? ROUND_TABLES : RECT_TABLES;
  const max = maxTableFootprint({ roomLengthFt, roomWidthFt });
  if (!max) return null;
  const fits = (t) => {
    const [w, l] = tableDims(t);
    return w <= max[0] && l <= max[1];
  };
  const needed = tables.find((t) => t.seats[0] >= people) ?? null;
  const largestFit = [...tables].reverse().find(fits) ?? null;
  const table = needed && fits(needed) ? needed : largestFit;
  return {
    max,
    needed,
    neededFits: Boolean(needed && fits(needed)),
    table,
    clearance: table ? tableClearance(table, { roomLengthFt, roomWidthFt }) : null,
  };
}

// ---------------------------------------------------------------------------
// Wall art
// ---------------------------------------------------------------------------

/** Gap between the furniture and the art's bottom edge, inches. */
export const ART_GAP_IN = { sofa: [8, 10], bed: [8, 12], console: [6, 10] };
export const ART_CENTER_IN = [57, 60];

/** Art (or the whole group) spans two-thirds to three-quarters of the furniture. */
export function artWidthOverFurniture(furnitureWidthIn) {
  return furnitureWidthIn > 0 ? [Math.floor((furnitureWidthIn * 2) / 3), Math.floor((furnitureWidthIn * 3) / 4)] : null;
}

/** On an open wall: 60–75 percent of the wall section. */
export function artWidthOnWall(wallWidthIn) {
  return wallWidthIn > 0 ? [Math.floor(wallWidthIn * 0.6), Math.floor(wallWidthIn * 0.75)] : null;
}

// ---------------------------------------------------------------------------
// Lampshades
// ---------------------------------------------------------------------------

/** Bottom about twice the base's widest part; height about two-thirds of the
 *  base (bottom of the lamp to the socket). */
export function lampshadeSize({ baseWidthIn, baseHeightIn }) {
  if (!(baseWidthIn > 0 && baseHeightIn > 0)) return null;
  return { bottomIn: Math.round(baseWidthIn * 2), heightIn: Math.round((baseHeightIn * 2) / 3) };
}

// ---------------------------------------------------------------------------
// Christmas trees (/ideas/christmas-tree-size-guide)
// ---------------------------------------------------------------------------

/** Headroom for the stand and a topper: "a foot of clearance covers both". */
export const TREE_HEADROOM_IN = 12;
/** "Leave at least 30 inches clear on any path people walk through." */
export const TREE_PATH_IN = 30;
/** Diameter as a share of height: full trees 55–75%, slim ones about half. Pencil trees are narrower still (no figure). */
export const TREE_WIDTH_SHARE = { full: [0.55, 0.75], slim: [0.5, 0.5] };
export const TREE_LIGHTS_PER_FT = 100;
export const TREE_ORNAMENTS_PER_FT = [10, 20];

/** The tallest tree for a ceiling: its height less a foot, in the half-foot steps trees are sold in. */
export function christmasTreeHeightFt(ceilingFt) {
  if (!(ceilingFt > 0)) return null;
  const ft = Math.floor((ceilingFt - TREE_HEADROOM_IN / 12) * 2) / 2;
  return ft > 0 ? ft : null;
}

/** Diameter at the widest point, inches, for a tree of this height and shape (null for pencil). */
export function christmasTreeWidthIn(heightFt, shape) {
  const share = TREE_WIDTH_SHARE[shape];
  if (!share || !(heightFt > 0)) return null;
  return share.map((x) => Math.round(heightFt * 12 * x));
}

// ---------------------------------------------------------------------------
// Coffee tables (/ideas/how-to-choose-a-coffee-table)
// ---------------------------------------------------------------------------

/** Space between the sofa and the table, and the walkway around it, inches. */
export const COFFEE_TABLE_GAP_IN = [14, 18];
export const COFFEE_TABLE_WALKWAY_IN = 30;

/**
 * About two-thirds of the sofa's length, and most people go a little shorter:
 * a 78–90 in. sofa gives 52–60 in. at two-thirds, while 44–54 in. reads relaxed.
 * The table's depth is roughly half its length.
 */
export function coffeeTableSize(sofaLengthIn) {
  if (!(sofaLengthIn > 0)) return null;
  const twoThirds = Math.round((sofaLengthIn * 2) / 3);
  const relaxed = [Math.round(sofaLengthIn * 0.56), Math.round(sofaLengthIn * 0.6)];
  return { twoThirds, relaxed, range: [relaxed[0], twoThirds], depth: [Math.round(relaxed[0] / 2), Math.round(twoThirds / 2)] };
}

/** Level with the sofa seat or up to two inches below it. */
export function coffeeTableHeight(seatHeightIn) {
  return seatHeightIn > 0 ? [Math.round(seatHeightIn - 2), Math.round(seatHeightIn)] : null;
}

// ---------------------------------------------------------------------------
// Bar and counter stools (/ideas/bar-counter-stool-height-guide)
// ---------------------------------------------------------------------------

/** Seat to the underside of the counter, inches. */
export const STOOL_GAP_IN = [9, 12];

/** The guide's surface heights and the seat each takes. */
export const STOOL_CLASSES = [
  { id: "chair", name: "dining chair", surface: [28, 30], seat: [17, 19] },
  { id: "counter", name: "counter stool", surface: [36, 36], seat: [24, 26] },
  { id: "bar", name: "bar stool", surface: [40, 42], seat: [28, 30] },
  { id: "spectator", name: "spectator stool", surface: [44, 47], seat: [33, 36] },
];

/** Width each stool needs along the counter, center to center, inches. */
export const STOOL_SPACING_IN = { backless: 24, back: 28, arms: 30 };

/**
 * Surface height minus a 9–12 in. gap is the seat height. The stool type is
 * the guide's class for that surface height (within an inch); an in-between
 * surface such as a 38 in. island matches none and wants an adjustable stool.
 * fitSeat is where the standard class and the 9–12 in. rule agree.
 */
export function stoolSeat(surfaceIn) {
  if (!(surfaceIn > 0)) return null;
  const seat = [surfaceIn - STOOL_GAP_IN[1], surfaceIn - STOOL_GAP_IN[0]];
  const off = (c) => Math.max(0, c.surface[0] - surfaceIn, surfaceIn - c.surface[1]);
  const overlap = (c) => Math.min(seat[1], c.seat[1]) - Math.max(seat[0], c.seat[0]);
  const match = STOOL_CLASSES.filter((c) => off(c) <= 1).sort((a, b) => off(a) - off(b) || overlap(b) - overlap(a))[0] ?? null;
  const lo = match ? Math.max(seat[0], match.seat[0]) : null;
  const hi = match ? Math.min(seat[1], match.seat[1]) : null;
  const fitSeat = match ? (lo <= hi ? [lo, hi] : match.seat) : null;
  return { seat, stool: match, fitSeat };
}

/** Each stool gets a zone as wide as its spacing, so the end stools sit half a zone in. */
export function stoolsThatFit(counterRunIn, style = "backless") {
  const spacing = STOOL_SPACING_IN[style];
  return counterRunIn > 0 && spacing ? Math.floor(counterRunIn / spacing) : 0;
}

// ---------------------------------------------------------------------------
// Getting a sofa through a doorway (/ideas/how-to-measure-for-furniture-delivery)
// ---------------------------------------------------------------------------

/** Clearance to leave everywhere for hands and maneuvering, inches. */
export const PATH_CLEARANCE_IN = 2;
/** Taking the door off its hinges gains about this much width, inches. */
export const DOOR_REMOVAL_GAIN_IN = 2;

/**
 * A sofa goes through on its back or side when its depth or height — the
 * smaller one — is narrower than the opening, or when angled, when its
 * diagonal depth is. Both with the 2 in. clearance; off the hinges, the
 * opening gains about 2 in.
 */
export function sofaThroughDoor({ doorWidthIn, sofaDepthIn, sofaHeightIn, diagonalIn }) {
  if (!(doorWidthIn > 0 && sofaDepthIn > 0 && sofaHeightIn > 0)) return null;
  const narrow = Math.min(sofaDepthIn, sofaHeightIn);
  const usable = doorWidthIn - PATH_CLEARANCE_IN;
  const passes = (w) => (narrow <= w ? "flat" : diagonalIn > 0 && diagonalIn <= w ? "angled" : null);
  const way = passes(usable);
  const withoutDoor = way ? null : passes(usable + DOOR_REMOVAL_GAIN_IN);
  return { way, withoutDoor, narrow, usable, maxNarrow: usable };
}

// ---------------------------------------------------------------------------
// End tables, nightstands and mirrors (product-page fit facts only)
//   end tables  -> /ideas/end-table-height-guide
//   nightstands -> /ideas/nightstand-height-guide
//   mirrors     -> /ideas/mirror-size-guide (same 2/3–3/4 width rule as art)
// ---------------------------------------------------------------------------

/** An end table's top within 2 in. of the sofa or chair arm, above or below. */
export const END_TABLE_ARM_TOLERANCE_IN = 2;
/** A nightstand's top within 2 in. of the mattress top, above or below. */
export const NIGHTSTAND_MATTRESS_TOLERANCE_IN = 2;
/** A mirror's bottom edge above the furniture it hangs over, inches. */
export const MIRROR_GAP_IN = [4, 10];

/** Table plus lamp (to the top of the shade), inches: puts the shade's bottom
 *  edge near seated eye level (/ideas/table-lamp-size-guide). */
export const TABLE_LAMP_TOTAL_IN = [54, 60];
/** The table lamp guide's surface heights, inches. */
export const LAMP_SURFACES = [
  { name: "end table", height: [20, 26] },
  { name: "nightstand", height: [24, 28] },
  { name: "console", height: [30, 34] },
  { name: "dresser", height: [30, 36] },
];
